import React, { useState } from "react";
import { FormHelperText, TextField, Typography } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { useDocumentUpload } from "@features/vessel-call/create/hooks/useDocumentUpload";
import { useCompleteShippingAgencyTransfer } from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/hooks/useCompleteShippingAgencyTransfer";
import { SasTokenPrefix } from "@services/user/types";
import { useTranslation } from "react-i18next";

interface RejectShippingAgencyTransferDrawerProps {
  isOpen: boolean;
  transferId: string;
  onClose: (drawerName: string) => void;
  onSuccess?: (message: string) => Promise<void> | void;
}

const LANG_BASE = "vessel-call.shipping-agency-transfer";
const SERVICE = "bff-vessel-call";
const REJECTION_REASON_MAX_LENGTH = 1000;

const RejectShippingAgencyTransferDrawer: React.FC<RejectShippingAgencyTransferDrawerProps> = ({
  isOpen,
  transferId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [rejectionReason, setRejectionReason] = useState("");

  const [error, setError] = useState<string | null>(null);
  const fileProfiles = useFileUploadProfiles();
  const attachmentProfile = fileProfiles.sideDrawerApprovalFile;
  const allowedExtensions = attachmentProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const {
    uploadEntries: internalAttachments,
    setUploadEntries: setInternalAttachments,
    handleUpload,
    handleRemove,
  } = useDocumentUpload("shippingAgencyTransferInternalAttachment", SasTokenPrefix.VESSEL_CALL);

  const handleRejectSuccess = async () => {
    await onSuccess?.(t(`${LANG_BASE}.notifications.transfer-rejected`));
    setRejectionReason("");
    handleRemove();
    setError(null);
    onClose("rejectDrawer");
  };

  const handleRejectError = (mutationError: any) => {
    setRejectionReason("");
    handleRemove();
    setError(null);
    onClose("rejectDrawer");

    notify({
      title: t("common.error"),
      description:
        mutationError?.response?.data?.detail ??
        mutationError?.message ??
        t("common.something-went-wrong"),
      type: "error",
    });
  };

  const { completeShippingAgencyTransfer } = useCompleteShippingAgencyTransfer({
    handleSuccess: handleRejectSuccess,
    handleError: handleRejectError,
  });

  const handleSubmit = () => {
    if (!rejectionReason.trim()) {
      setError(t(`${LANG_BASE}.rejection-reason-required`));
      return;
    }

    if (rejectionReason.trim().length > REJECTION_REASON_MAX_LENGTH) {
      setError(
        t(`${LANG_BASE}.rejection-reason-max-length-error`, {
          max: REJECTION_REASON_MAX_LENGTH,
        }),
      );
      return;
    }

    const attachmentUrl =
      internalAttachments[0] && "url" in internalAttachments[0]
        ? internalAttachments[0].url
        : undefined;
    const attachmentName =
      internalAttachments[0] && "name" in internalAttachments[0]
        ? internalAttachments[0].name
        : undefined;

    completeShippingAgencyTransfer({
      id: transferId,
      service: SERVICE,
      payload: {
        status: "REJECTED",
        rejectionReason: rejectionReason.trim(),
        rejectionAttachmentsUrl: attachmentUrl,
        rejectionAttachmentsName: attachmentName,
      },
    });
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      title={t(`${LANG_BASE}.reject-transfer-title`)}
      submitLabel={t(`${LANG_BASE}.flow-buttons.reject-button`)}
      onClose={() => {
        setRejectionReason("");
        handleRemove();
        setError(null);
        onClose("rejectDrawer");
      }}
      onSubmit={handleSubmit}
    >
      <div className="flex-1 max-w-sm">
        <div className="px-6">
          <Typography variant="body1" sx={{ color: "text.primary", pb: 2 }}>
            {t(`${LANG_BASE}.reject-transfer-subtitle`)}
          </Typography>
          <TextField
            placeholder={t(`${LANG_BASE}.rejection-reason`)}
            multiline
            minRows={4}
            fullWidth
            value={rejectionReason}
            inputProps={{ "data-testid": "shipping-agency-transfer-rejection-reason-input" }}
            onChange={(event) => {
              const nextValue = event.target.value;

              if (nextValue.length > REJECTION_REASON_MAX_LENGTH) {
                setRejectionReason(nextValue.slice(0, REJECTION_REASON_MAX_LENGTH));
                setError(
                  t(`${LANG_BASE}.rejection-reason-max-length-error`, {
                    max: REJECTION_REASON_MAX_LENGTH,
                  }),
                );
                return;
              }

              setRejectionReason(nextValue);
              setError(null);
            }}
          />
          {error && (
            <FormHelperText error data-testid="shipping-agency-transfer-rejection-reason-error">
              {error}
            </FormHelperText>
          )}
          <div className="mt-4">
            <DocumentUpload
              label={t(`${LANG_BASE}.rejection-attachments`)}
              testId="shipping-agency-transfer-internal-attachment"
              name="shippingAgencyTransferInternalAttachment"
              isSideDrawer
              maxFiles={1}
              allowedExtensions={allowedExtensions}
              conditions={attachmentProfile}
              files={internalAttachments}
              setFiles={setInternalAttachments}
              uploadDescription={t("upload.dragOrDropDescription")}
              onUpload={handleUpload}
            />
            {internalAttachments[0] && (
              <div className="flex-1 mt-4">
                <SingleFile
                  file={internalAttachments[0]}
                  fieldName="shippingAgencyTransferInternalAttachment"
                  setFiles={() => {
                    handleRemove();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </SideDrawer>
  );
};

export default RejectShippingAgencyTransferDrawer;
